// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { organizationUserSchema, personSchema } from '@/account/api/types';
import { responseMetadataSchema } from '@/api/types';
import { chargeSchema } from '@/billing/api/types';
import { billingFrequencySchema } from '@/constants/billing-frequency';
import { chargeModeSchema } from '@/constants/charge-mode';
import { fulfillmentStatusSchema } from '@/constants/fulfillment-status';
import { currencySchema } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { paymentTermSchema } from '@/payment-term/api/types';
import {
  sparseCustomerSchema,
  sparseItemSchema,
  sparseLocationWithParentSchema,
  sparseProjectSchema,
} from '@/sparse/api/types';
import { dollarStringSchema } from '@/utilities/currency';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { zodCatch } from '@/utilities/zod-catch';
import { z } from 'zod';

const projectAssetAssignmentSchema = sparseItemSchema.extend({
  assignedCount: z.number(),
  requestedCount: z.number(),
});

export type ProjectAssetAssignment = z.infer<
  typeof projectAssetAssignmentSchema
>;

const billingAmountSchema = z.string().transform((value, ctx) => {
  const amount = parseCurrencyAmount(value);
  if (amount === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Amount must be a valid number',
    });
    return z.NEVER;
  }

  return amount;
});

const currencyBillingSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  planned: billingAmountSchema,
  billable: billingAmountSchema,
  invoiced: billingAmountSchema,
  cancelled: billingAmountSchema,
  total: billingAmountSchema,
});

export type CurrencyBillingSummary = z.infer<
  typeof currencyBillingSummarySchema
>;

export const projectBillingAggregateResponseSchema = z.object({
  data: z.object({
    byCurrency: z
      .array(currencyBillingSummarySchema)
      .nullable()
      .transform((v) => v ?? []),
  }),
  metadata: responseMetadataSchema,
});

export type ProjectBillingAggregateResponse = z.infer<
  typeof projectBillingAggregateResponseSchema
>;

/**
 * Schema for a project line item.
 * Uses nested item structure where line item binding identifiers (id, key) are at the
 * top level, and catalog item properties are nested under `item`.
 */
export const transactionTypeSchema = z.enum(['SALE', 'SERVICE']);

export type TransactionType = z.infer<typeof transactionTypeSchema>;

/** Typed constant object for TransactionType enum values. */
export const TRANSACTION_TYPE = transactionTypeSchema.enum;

export const PROJECT_NAME_MAX_LENGTH = 255;
export const PROJECT_DESCRIPTION_MAX_LENGTH = 65536;
export const PROJECT_NAME_MAX_LENGTH_MESSAGE =
  'Name cannot exceed 255 characters';

// Milestone phases are informational: the stored plan describes the
// expected breakdown, but the generated charges are not linked back to
// any particular phase. Users can freely edit phases; the charge list
// only regenerates when the user explicitly clicks Regenerate, and that
// button is disabled when any existing charge is invoiced.
const milestonePhasePercentageEntrySchema = z.object({
  name: z.string().min(1),
  percentage: z.coerce.number().min(0).max(100),
});

/**
 * Milestone phases on a billing plan. Entries carry a percentage of the
 * line-item total; `mode` is retained as a single-value tag so the stored
 * blob stays a tagged object.
 */
const milestonePhasesSchema = z.object({
  mode: z.literal('PERCENTAGE'),
  entries: z.array(milestonePhasePercentageEntrySchema).min(1),
});

export type MilestonePhases = z.infer<typeof milestonePhasesSchema>;

export const projectLineItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  item: sparseItemSchema,
  quantity: z.number().nullable(),
  unitPrice: currencySchema.nullable(),
  billingFrequency: billingFrequencySchema.nullable().catch(null),
  details: z.string().nullable(),
  location: sparseLocationWithParentSchema.nullable(),
  transactionType: transactionTypeSchema,
  startOn: dateSchema.nullable().catch(null),
  endOn: dateSchema.nullable().catch(null),
  pricingMode: z.string().nullable().catch(null),
  chargeSchedule: z.string().nullable().catch(null),
  chargeFrequency: z.string().nullable().catch(null),
  invoiceFrequency: z.string().nullable().catch(null),
  invoiceMode: z.string().nullable().catch(null),
  fixedPrice: currencySchema.nullable().catch(null),
  totalOverride: currencySchema.nullable().catch(null),
  milestonePhases: milestonePhasesSchema.nullable().catch(null),
  aggregationKey: z.string(),
  phaseRowNumber: z.number(),
  chargeMode: chargeModeSchema,
  activationDate: dateSchema.nullable(),
  charges: chargeSchema
    .array()
    .nullable()
    .transform((v) => v ?? []),
  hasInvoicedCharges: z.boolean(),
});

export type ProjectLineItem = z.infer<typeof projectLineItemSchema>;

const projectCostDataTimeSchema = z.object({
  hours: z.string(),
  minutes: z.number(),
  rate: dollarStringSchema.nullable(),
  rateCurrency: isoCurrencyCodeSchema.nullable(),
  employee: personSchema,
});

const costTypeSchema = z.enum(['MISC', 'TIME']);

const _projectCostDataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('TIME'),
    amount: currencySchema.nullable(),
    costData: projectCostDataTimeSchema,
  }),
  z.object({
    type: z.literal('MISC'),
    amount: currencySchema.nullable(),
    costData: z.null(),
  }),
]);

// Add schema for project costs
export const projectCostSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    description: z.string().nullable(),
    createdAt: dateTimeSchema,
    createdBy: organizationUserSchema,
    lastUpdatedAt: dateTimeSchema,
    lastUpdatedBy: organizationUserSchema,
    costIncurredOn: dateSchema,
    type: costTypeSchema,
    attachedToPerson: personSchema.nullable(),
    attachedToProject: sparseProjectSchema.nullable(),
    category: z.string().nullish(),
  })
  .and(_projectCostDataSchema);

export type ProjectCost = z.infer<typeof projectCostSchema>;

export const projectBillingInfoSchema = z.object({
  billingAddress: z.string().nullable(),
  billingContactName: z.string().nullable(),
  billingContactEmail: z.string().nullable(),
  billingContactPhone: z.string().nullable(),
});

export type ProjectBillingInfo = z.infer<typeof projectBillingInfoSchema>;

/**
 * One row of the project fulfillment summary: project-requested demand
 * (max-concurrent across line item windows) joined with aggregate quantities
 * summed across every non-cancelled fulfillment order on the project.
 */
const projectFulfillmentBySkuRowSchema = z.object({
  sku: z.string(),
  itemName: z.string(),
  itemId: z.string(),
  projectRequested: z.number(),
  foRequested: z.number(),
  pending: z.number(),
  shipped: z.number(),
  delivered: z.number(),
  netNeedQuantity: z.number(),
});

export type ProjectFulfillmentBySkuRow = z.infer<
  typeof projectFulfillmentBySkuRowSchema
>;

export const projectFulfillmentBySkuSchema = z.object({
  rows: z.array(projectFulfillmentBySkuRowSchema),
});

export type ProjectFulfillmentBySku = z.infer<
  typeof projectFulfillmentBySkuSchema
>;

const projectCurrencySourceSchema = z.enum([
  'INVOICED_CHARGE',
  'PROJECT_VALUE',
  'LINE_ITEMS',
  'DISCOUNT',
]);

export type ProjectCurrencySource = z.infer<typeof projectCurrencySourceSchema>;

// The project's single currency and the field that established it; null when no
// currency-bearing field is set.
const projectCurrencySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  source: projectCurrencySourceSchema,
});

export const projectSchema = sparseProjectSchema.extend({
  description: z.string().nullable().catch(null),
  fileCount: z.number(),
  urlLinkCount: z.number(),
  noteCount: z.number(),
  customer: sparseCustomerSchema.nullable(),
  items: z.array(projectLineItemSchema).default([]),
  assignments: z
    .array(projectAssetAssignmentSchema)
    .nullable()
    .transform((v) => v ?? []),
  defaultLocation: sparseLocationWithParentSchema.nullable(),
  locations: z
    .array(sparseLocationWithParentSchema)
    .nullable()
    .transform((v) => v ?? []),
  totalContractValue: currencySchema.nullable(),
  previousProject: sparseProjectSchema.nullable().catch(null),
  costCount: z.number(),
  timeActivityCount: z.number(),
  ticketCount: z.number(),
  chargeCount: z.number(),
  invoicedChargeCount: z.number(),
  paymentTerm: paymentTermSchema.nullable().catch(null),
  billingAddress: z.string().nullable().catch(null),
  billingContactName: z.string().nullable().catch(null),
  billingContactEmail: z.string().nullable().catch(null),
  billingContactPhone: z.string().nullable().catch(null),
  purchaseOrderNumber: z.string().nullable().catch(null),
  memo: z.string().nullable().catch(null),
  discount: currencySchema.nullable(),
  contractSignedOn: dateSchema.nullable(),
  installDate: dateSchema.nullable(),
  siteVisitDate: dateSchema.nullable(),
  trainingDate: dateSchema.nullable(),
  goLiveDate: dateSchema.nullable(),
  acceptanceDate: dateSchema.nullable(),
  totalAssignmentSegmentCount: z.number(),
  totalUniqueAssetCount: z.number(),
  annualRecurringRevenue: z
    .array(currencySchema)
    .nullable()
    .transform((v) => v ?? []),
  currency: projectCurrencySchema.nullable(),
  activeWorkflow: z
    .object({
      id: z.string(),
      key: z.string(),
      name: z.string(),
      stages: z.array(
        z.object({
          id: z.string(),
          key: z.string(),
          name: z.string(),
          order: z.number(),
          active: z.boolean(),
        })
      ),
    })
    .nullable()
    .catch(zodCatch(null)),
  projectFulfillmentStatus: fulfillmentStatusSchema,
});

export type Project = z.infer<typeof projectSchema>;
